import {
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'

import type { DateRangeKey, DateRangeOption } from 'state/dashboard/types'
import type { CustomDateType, DateRangePickerOption, DateRangePickerOptions } from './types'

export const DEFAULT_DATE_RANGE_KEY: DateRangeKey = 'last-90-days'

export const customOption: DateRangePickerOption = { label: '', key: 'custom' }

export const DEFAULT_DATE_RANGE_OPTION: DateRangePickerOption = { label: 'Last 90 days (from yesterday)', key: DEFAULT_DATE_RANGE_KEY }

export const getDateRangeOptions = (): DateRangePickerOptions => {
  const today = new Date()
  const aMonthBack = subMonths(today, 1)
  const aYearBack = subYears(today, 1)
  return [
    { label: `This month (${format(today, 'MMM')})`, key: 'this-month' },
    { label: `Last month (${format(aMonthBack, 'MMM')})`, key: 'last-month' },
    { label: 'Last 30 days (from yesterday)', key: 'last-30-days' },
    DEFAULT_DATE_RANGE_OPTION,
    { label: 'Last 12 months', key: 'last-12-months' },
    { label: `This year (${format(today, 'yyyy')})`, key: 'this-year' },
    { label: `Last year (${format(aYearBack, 'yyyy')})`, key: 'last-year' },
    customOption,
  ]
}

const buildDateRangeObj = (startDate: Date, endDate: Date) => (
  { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
)

export const getThisMonthRange = (today: Date) => buildDateRangeObj(startOfMonth(today), endOfDay(today))
export const getLastMonthRange = (today: Date) => {
  const aMonthBack = subMonths(today, 1)
  return buildDateRangeObj(startOfMonth(aMonthBack), endOfMonth(aMonthBack))
}
export const getLast30DaysRange = (today: Date) => {
  const yesterday = subDays(today, 1)
  return buildDateRangeObj(subDays(startOfDay(today), 30), endOfDay(yesterday))
}
export const getLast90DaysRange = (today: Date) => {
  const yesterday = subDays(today, 1)
  return buildDateRangeObj(subDays(startOfDay(today), 90), endOfDay(yesterday))
}
export const getLast12MonthsRange = (today: Date) => {
  const aMonthBack = subMonths(today, 1)
  return buildDateRangeObj(startOfMonth(subMonths(today, 12)), endOfMonth(aMonthBack))
}
export const getThisYearRange = (today: Date) => buildDateRangeObj(startOfYear(today), endOfDay(today))
export const getLastYearRange = (today: Date) => {
  const aYearBack = subYears(today, 1)
  return buildDateRangeObj(startOfYear(aYearBack), endOfYear(aYearBack))
}
export const getCustomDateRange = ({ startDate, endDate }: { startDate: string | Date; endDate: string | Date }) => {
  return buildDateRangeObj(
    startOfDay(new Date(startDate)),
    endOfDay(new Date(endDate))
  )
}

const getLabelFormattedDate = (dateTime: string) => (
  format(new Date(dateTime), "d LLL yyyy")
)

export const customDateRangeLabel = (dates: { startDate: string; endDate: string }): string => {
  const startDate = getLabelFormattedDate(dates.startDate)
  const endDate = getLabelFormattedDate(dates.endDate)
  const startListReversed = startDate.split(' ').reverse()
  const endListReversed = endDate.split(' ').reverse()
  const matchingEntries: string[] = []
  for (let i = 0; i < startListReversed.length; i++) {
    const item = startListReversed[i]
    if (endListReversed[i] != item) break
    matchingEntries.unshift(item)
  }
  const matchingPart = matchingEntries.join(' ').trim()
  return matchingPart === startDate ?
    matchingPart :
    `${startDate.replace(matchingPart, '').trim()} - ${endDate}`
}

export const getDateRangeFromOption = (dateRangeChoice: Pick<DateRangeOption, 'key' | 'label'>, customDate: CustomDateType = {}): DateRangeOption => {
  const today = new Date()
  switch (dateRangeChoice.key) {
    case 'this-month':
      return { ...dateRangeChoice, value: getThisMonthRange(today) }
    case 'last-month':
      return { ...dateRangeChoice, value: getLastMonthRange(today) }
    case 'last-30-days':
      return { ...dateRangeChoice, value: getLast30DaysRange(today) }
    case 'last-90-days':
      return { ...dateRangeChoice, value: getLast90DaysRange(today) }
    case 'last-12-months':
      return { ...dateRangeChoice, value: getLast12MonthsRange(today) }
    case 'this-year':
      return { ...dateRangeChoice, value: getThisYearRange(today) }
    case 'last-year':
      return { ...dateRangeChoice, value: getLastYearRange(today) }
    case 'custom': {
      const { startDate, endDate } = customDate
      if (startDate !== undefined && endDate !== undefined) {
        const dateRangeValue = getCustomDateRange({ startDate, endDate })
        return { ...dateRangeChoice, label: customDateRangeLabel(dateRangeValue), value: dateRangeValue }
      } else {
        return { ...dateRangeChoice, value: { startDate: '', endDate: '' } }
      }
    }
    default:
      return { ...dateRangeChoice, value: { startDate: '', endDate: '' } }
  }
}
